import { useState, type FormEvent } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  type Location,
} from 'react-router-dom';
import { GoldButton } from '@/components/common/GoldButton';
import { useAuth, EmailNotVerifiedError } from '@/context/AuthContext';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { t } from '@/i18n';
import {
  canonicalizeEmailForSubmit,
  getEmailValidationError,
  isSupportedEmail,
  MAX_EMAIL_LENGTH,
} from '@/utils/authValidation';
import styles from './LoginForm.module.css';

type LocationState = { from?: string } | null;

export function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });
  const [submitting, setSubmitting] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);

  const navigate = useNavigate();
  const location = useLocation() as Location & { state: LocationState };
  const { login, resendVerification } = useAuth();
  const onApiError = useApiErrorToast();

  const emailErr = touched.email && !isSupportedEmail(email);
  const passErr = touched.password && password.length === 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    const submittedEmail = canonicalizeEmailForSubmit(email);
    if (!isSupportedEmail(email) || !password || submitting) return;
    setUnverified(false);
    setSubmitting(true);
    try {
      await login({ email: submittedEmail, password });
      const target = location.state?.from ?? '/';
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof EmailNotVerifiedError) {
        setUnverified(true);
      } else {
        onApiError(err, t.auth.wrongCredentials);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resending || resendCooldown) return;
    setResending(true);
    try {
      await resendVerification(canonicalizeEmailForSubmit(email));
      setResendCooldown(true);
      setTimeout(() => setResendCooldown(false), 60_000);
    } catch (err) {
      onApiError(err, t.auth.resendFailed);
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <div className={styles.grp}>
        <div className={styles.lbl}>{t.common.emailLabel}</div>
        <input
          className={[styles.inp, emailErr ? styles.err : '']
            .filter(Boolean)
            .join(' ')}
          autoComplete="email"
          autoCapitalize="none"
          inputMode="email"
          spellCheck={false}
          type="email"
          required
          maxLength={MAX_EMAIL_LENGTH}
          placeholder={t.common.emailPlaceholder}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setUnverified(false);
          }}
          onBlur={() => setTouched((value) => ({ ...value, email: true }))}
          disabled={submitting}
        />
        <div
          className={[styles.e, emailErr ? styles.eShow : '']
            .filter(Boolean)
            .join(' ')}
        >
          {getEmailValidationError(email) === 'internationalLocalPart'
            ? t.common.emailInternationalUnsupported
            : t.common.emailInvalid}
        </div>
      </div>
      <div className={styles.grp}>
        <div className={styles.lbl}>{t.common.passwordLabel}</div>
        <input
          className={[styles.inp, passErr ? styles.err : '']
            .filter(Boolean)
            .join(' ')}
          autoComplete="current-password"
          type="password"
          placeholder={t.common.passwordPlaceholder}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={() => setTouched((value) => ({ ...value, password: true }))}
          disabled={submitting}
          required
        />
        <div
          className={[styles.e, passErr ? styles.eShow : '']
            .filter(Boolean)
            .join(' ')}
        >
          {t.common.passwordRequired}
        </div>
      </div>
      {unverified && (
        <div className={styles.unverifiedBanner}>
          <span>{t.auth.emailNotVerified}</span>
          <button
            type="button"
            className={styles.resendLink}
            onClick={handleResend}
            disabled={resending || resendCooldown}
          >
            {resending
              ? t.common.sending
              : resendCooldown
                ? t.common.sent
                : t.auth.resendEmail}
          </button>
          <Link
            to={`/verify-email?email=${encodeURIComponent(
              canonicalizeEmailForSubmit(email),
            )}${resendCooldown ? '&sent=1' : ''}`}
            className={styles.resendLink}
          >
            {t.auth.enterCode}
          </Link>
        </div>
      )}
      <GoldButton type="submit" disabled={submitting}>
        {submitting ? t.auth.loginSubmitting : t.auth.login}
      </GoldButton>
      <div className={styles.forgotWrap}>
        <Link to="/forgot-password" className={styles.forgotLink}>
          {t.auth.forgotPassword}
        </Link>
      </div>
    </form>
  );
}
